import { type RefObject, useLayoutEffect } from 'react';

import type { TWidthFromAnchorMode } from '../popover/types';

import { setStyle } from './set-style';

/**
 * Sets the width of a popover element relative to its anchor element.
 *
 * - `'match-anchor'` → `width: anchor-size(width)` - popover matches anchor width exactly.
 * - `'min-anchor'`   → `min-inline-size: anchor-size(width)` - popover is at least as wide as the
 *   anchor, and can grow wider if its content requires it. The anchor width is the ONLY floor: see
 *   `notes/decisions/width-from-anchor-floors.md` for why this mode deliberately does not also floor
 *   the popover at its own content width.
 * - `'none'`         → `min-inline-size: max-content` - no anchor-relative width, but floored at the
 *   popover's own content width (default behaviour)
 *
 * Uses CSS `anchor-size(width)` when the browser supports it, falling back to a
 * one-off measurement of `anchor.offsetWidth`.
 *
 * Designed to be composed alongside `useAnchorPosition` for top-layer popovers.
 */
export function useWidthFromAnchor({
	mode,
	popoverRef,
	anchorRef,
	isOpen,
}: {
	mode: TWidthFromAnchorMode;
	popoverRef: RefObject<HTMLElement | null>;
	anchorRef: RefObject<HTMLElement | null>;
	isOpen: boolean;
}): void {
	useLayoutEffect(() => {
		const node = popoverRef.current;
		if (!node) {
			return;
		}

		// Feature-detect `anchor-size()` specifically (not just `anchor-name`)
		// so the JS fallback fires correctly in browsers that support anchor
		// positioning but not anchor sizing.
		const supportsAnchorSize =
			typeof CSS !== 'undefined' &&
			typeof CSS.supports === 'function' &&
			CSS.supports('width', 'anchor-size(width)');

		if (mode === 'match-anchor') {
			// Set an exact width matching the anchor.
			// `min-inline-size` is intentionally NOT set here - it would override the
			// explicit width constraint, allowing the popover to grow past the anchor's width.
			if (supportsAnchorSize) {
				return setStyle({
					element: node,
					styles: [{ property: 'width', value: 'anchor-size(width)' }],
				});
			}

			// JS fallback: one-off read of the anchor's width.
			const anchor = anchorRef.current;
			if (anchor) {
				return setStyle({
					element: node,
					styles: [{ property: 'width', value: `${anchor.offsetWidth}px` }],
				});
			}

			return;
		}

		if (mode === 'min-anchor') {
			// One declaration, one floor: the anchor width, as a lower bound and not an
			// exact size, which is what separates 'min-anchor' from 'match-anchor'.
			//
			// This mode deliberately does NOT also floor the popover at its own content
			// width. Mode 'none' does, to make a too-narrow span overflow and drive
			// `position-try-fallbacks`, but that floor can only be expressed on
			// `min-inline-size` or on `width`, and both collide with the anchor floor here:
			// `min-inline-size` IS `min-width` in a horizontal writing mode, and `width` is
			// an exact size, so it would cap the popover and override a consumer width.
			// The anchor floor already guarantees a usable width for this mode, so the
			// popover is allowed to wrap down to it. See
			// `notes/decisions/width-from-anchor-floors.md`.
			if (supportsAnchorSize) {
				return setStyle({
					element: node,
					styles: [{ property: 'min-inline-size', value: 'anchor-size(width)' }],
				});
			}

			const anchor = anchorRef.current;
			if (anchor) {
				return setStyle({
					element: node,
					styles: [{ property: 'min-inline-size', value: `${anchor.offsetWidth}px` }],
				});
			}

			// No anchor to measure in the JS fallback, so there is no floor to apply.
			return;
		}

		// 'none' has no anchor floor to collide with, so it can carry the content floor.
		// `@atlaskit/popper` depends on the property: its fit-to-viewport mode neutralises
		// this floor with `min-inline-size: 0`.
		return setStyle({
			element: node,
			styles: [{ property: 'min-inline-size', value: 'max-content' }],
		});
	}, [mode, popoverRef, anchorRef, isOpen]);
}
